import { useMemo, useState } from 'react';
import type { AgentNetworkNode, RunStageName, RunStageRecord } from '@orqforge/shared';

const STAGE_FLOW: RunStageName[] = [
  'Lobby',
  'Ingest',
  'Triage',
  'Plan',
  'Hierarchy',
  'Execute',
  'Post-Exec',
  'Review',
  'Publish',
  'Learn',
  'Done',
];

export interface StageViewEntry {
  stage: RunStageName;
  status: string;
  elapsedMs: number;
  tokens: number;
}

export function buildStageView(stages: RunStageRecord[]): StageViewEntry[] {
  const byStage = new Map(stages.map((stage) => [stage.stage, stage]));
  return STAGE_FLOW.map((stage) => {
    const row = byStage.get(stage);
    return {
      stage,
      status: row?.status ?? 'pending',
      elapsedMs: row?.elapsedMs ?? 0,
      tokens: row?.tokens ?? 0,
    };
  });
}

function nodeStatus(node: AgentNetworkNode): string {
  return node.childStatus ?? node.status;
}

function isNodeActive(node: AgentNetworkNode): boolean {
  const status = nodeStatus(node);
  return status === 'queued' || status === 'working' || status === 'running' || status === 'pending';
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export function RunProgress({ stages, nodes }: { stages: RunStageRecord[]; nodes: AgentNetworkNode[] }) {
  const [expanded, setExpanded] = useState(true);
  const stageView = useMemo(() => buildStageView(stages), [stages]);
  const doneCount = stageView.filter((stage) => stage.status === 'done' || stage.status === 'skipped').length;
  const summary = useMemo(() => {
    const active = stageView.find((stage) => stage.status === 'active');
    const working = nodes.filter((node) => isNodeActive(node)).length;
    if (active && working > 0) return `${active.stage}: ${working} agent${working === 1 ? '' : 's'} working`;
    if (active) return `${active.stage}...`;
    return 'Starting...';
  }, [stageView, nodes]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white/80 dark:border-gray-700 dark:bg-gray-900/80" data-testid="run-progress">
      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm" onClick={() => setExpanded((value) => !value)}>
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <span className="font-medium">{summary}</span>
        <span className="ml-auto text-[11px] text-gray-400">
          {doneCount}/{stageView.length} stages
        </span>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 px-3 py-2 dark:border-gray-700">
          <ul className="flex flex-col gap-1">
            {stageView.map((stage) => (
              <li key={stage.stage}>
                <div className="flex items-center gap-2 text-xs" data-testid={`progress-stage-${stage.stage}`}>
                  <span className={stage.status === 'done' ? 'text-green-600' : stage.status === 'active' ? 'font-medium' : 'text-gray-400'}>
                    {stage.stage}
                  </span>
                  {stage.status === 'done' && stage.elapsedMs > 0 && <span className="text-gray-400">{formatElapsed(stage.elapsedMs)}</span>}
                  {stage.status === 'active' && <span className="text-blue-600">running</span>}
                  {stage.status === 'skipped' && <span className="text-gray-400 line-through">skipped</span>}
                </div>
                {stage.stage === 'Execute' &&
                  nodes.map((node) => (
                    <div key={node.delegationId} className="ml-6 flex items-center gap-2 text-xs" data-testid={`progress-delegate-${node.delegationId}`}>
                      <span>{node.toAgentName || node.toAgentId.slice(0, 8)}</span>
                      <span className="text-gray-400">{node.childStage || nodeStatus(node)}</span>
                    </div>
                  ))}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
